import { useCallback, useContext, useEffect, useState } from 'react'
import GeoContext from 'contexts/geoContext'
import useTabVisibility from 'hooks/useTabVisibility'
import { multiCartRefreshFlightItemEvent } from 'analytics/snowplow/events'
import useFeatureFlag from 'hooks/Optimizely/useFeatureFlag'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import { MultiCartContext } from 'machines/cart/MultiCartMachineProvider'
import { refreshMultiCartFlightItem } from './refreshMultiCartFlightItem'
import { CHARTER } from 'constants/provider'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { CHECKOUT_UPDATE_ITEM } from 'actions/actionConstants'
import { patchItemsInMultiCart } from 'api/cart/patchItemsInMultiCart'
import { isFlightItem } from 'lib/checkout/checkoutUtils'
import { canRequestFlightLuxPlusPricing } from 'lib/flights/flightLuxPlusPricing'

type FlightDataStatus = 'valid' | 'expired' | 'refreshing' | 'refreshFailed'

// setTimeout stores its delay as a 32-bit signed int; values above
// 2^31-1 overflow to 0 and fire immediately. Clamp to the max safe value.
const MAX_TIMEOUT_MS = 2 ** 31 - 1

function isFlightExpired(expiry: number | undefined) {
  return !!expiry && Date.now() >= expiry
}

function getCheckoutFlightItem(multiCartItem: App.MultiCartFlightItem): App.Checkout.FlightItem {
  const item = multiCartItem.item
  if (!item || !isFlightItem(item)) {
    throw new Error('Multi-cart flight item did not contain a checkout flight item')
  }

  return item
}

interface UseRefreshMultiCartFlightItemOptions {
  /**
   * Whether this flight is eligible for Maldives bundle pricing (no 15% markup).
   * When true, refresh will use source='charter' to get bundle pricing.
   * When false/undefined, refresh will use default pricing (with 15% markup for charter flights).
   */
  isBundleEligible?: boolean
}

function useRefreshMultiCartFlightItem(
  multiCartItem: App.MultiCartFlightItem,
  options: UseRefreshMultiCartFlightItemOptions = {},
): {
  dataStatus: FlightDataStatus
  initiateRefresh: () => void
  refreshStartedAt: number
} {
  const { isBundleEligible = false } = options
  const item = getCheckoutFlightItem(multiCartItem)
  const { expiry } = item
  const { currentRegionCode, currentCurrency } = useContext(GeoContext)

  const dispatch = useAppDispatch()
  const multiCartActorRef = MultiCartContext.useActorRef()
  const [dataStatus, setStatus] = useState<FlightDataStatus>(() => isFlightExpired(expiry) ? 'expired' : 'valid')
  const [refreshStartedAt, setRefreshStartedAt] = useState(0)

  const tabIsVisible = useTabVisibility()

  // For charter flights, determine pricing context based on bundle eligibility
  // - isBundleEligible=true: use source='charter' for no 15% markup (bundle pricing)
  // - isBundleEligible=false: use source=undefined for 15% markup (standalone pricing)
  const isCharterFlight = item.flights?.[0]?.provider === CHARTER
  const shouldUseBundlePricing = isCharterFlight && isBundleEligible
  const isBundledFlow = shouldUseBundlePricing || !!item.bundledItemIds?.length
  const enableSabreContent = useFeatureFlag(OptimizelyFeatureFlags.flightsEnableSabreContent)
  const standaloneLuxPlusPricingEnabled = useFeatureFlag(OptimizelyFeatureFlags.flightsLuxPlusPricing)
  const bundledLuxPlusPricingEnabled = useFeatureFlag(OptimizelyFeatureFlags.flightsBundledLuxPlusPricing)
  const enableStandaloneLuxPlusPricing = !isBundledFlow && standaloneLuxPlusPricingEnabled
  const enableBundledLuxPlusPricing = isBundledFlow && bundledLuxPlusPricingEnabled
  const enableLuxPlusPricing = isBundledFlow ? !!enableBundledLuxPlusPricing : !!enableStandaloneLuxPlusPricing
  const requestLuxPlusPricing = useAppSelector(state => canRequestFlightLuxPlusPricing(state, {
    enabled: enableLuxPlusPricing,
    isBundledFlow,
  }))
  const email = useAppSelector(state => state.auth.account.email)

  useEffect(() => {
    if (expiry && dataStatus === 'valid' && tabIsVisible) {
      const millisecondsUntilExpiry = expiry - Date.now()

      if (millisecondsUntilExpiry > 0) {
        const timeout = setTimeout(() => {
          setStatus('expired')
        }, Math.min(millisecondsUntilExpiry, MAX_TIMEOUT_MS))
        return () => clearTimeout(timeout)
      } else {
        setStatus('expired')
      }
    }
  }, [expiry, dataStatus, tabIsVisible])

  const initiateRefresh = useCallback(async() => {
    const { flights, occupants } = item

    if (!flights?.length || !occupants) {
      return
    }

    multiCartRefreshFlightItemEvent(item)
    setStatus('refreshing')
    setRefreshStartedAt(Date.now())

    try {
      const activeCartId = multiCartActorRef.getSnapshot().context.activeCartId

      const result = await refreshMultiCartFlightItem({
        multiCartItem,
        source: shouldUseBundlePricing ? CHARTER : undefined,
        currentCurrency,
        currentRegionCode,
        enableSabreContent,
        useCharterDateMatching: isCharterFlight, // Use date matching for charter flights
        email,
        requestLuxPlusPricing,
      })
      const updatedItem = result.updatedMultiCartItem.item
      const updatedFlightItem = updatedItem && isFlightItem(updatedItem) ? updatedItem : undefined
      const bundledItemIds = result.updatedMultiCartItem.bundledItemIds ?? updatedFlightItem?.bundledItemIds ?? multiCartItem.bundledItemIds ?? []
      const cartItemsState = multiCartActorRef.getSnapshot().context.cartItems
      const activeCartItems = activeCartId && cartItemsState.status === 'loaded' ? Object.values(cartItemsState.data[activeCartId] ?? {}) : []
      const savedBundledItemIds = activeCartId ? activeCartItems
        .filter(cartItem => cartItem.state === 'save_for_later')
        .filter(cartItem => bundledItemIds.includes(cartItem.item.itemId))
        .map(cartItem => cartItem.id) : []

      if (activeCartId && savedBundledItemIds.length > 0) {
        await patchItemsInMultiCart(activeCartId, {
          itemIds: savedBundledItemIds,
          saveForLater: false,
          skipNotification: true,
        })
      }

      // Update XState with the refreshed multi-cart item
      if (activeCartId) {
        multiCartActorRef.send({
          type: 'update cart item',
          cartId: activeCartId,
          multiCartItem: result.updatedMultiCartItem,
        })

        if (savedBundledItemIds.length > 0) {
          multiCartActorRef.send({
            type: 'update cart items save for later',
            itemIds: savedBundledItemIds,
            saveForLater: false,
          })
        }
      }

      // Also update Redux checkout.cart.items to keep them in sync
      // Note: Only dispatch if item already exists in Redux (updateCheckoutItem adds new items if not found)
      if (updatedFlightItem) {
        dispatch((reduxDispatch, getState) => {
          const checkoutItems = getState().checkout.cart.items
          const existsInRedux = checkoutItems.some(i => i.itemId === updatedFlightItem.itemId)
          if (existsInRedux) {
            reduxDispatch({ type: CHECKOUT_UPDATE_ITEM, item: updatedFlightItem })
          }
        })
      }

      setStatus('valid')
    } catch {
      // log error
      multiCartRefreshFlightItemEvent(item, 'failed')
      setStatus('refreshFailed')
      return
    }
  }, [multiCartActorRef, currentCurrency, currentRegionCode, item, multiCartItem, shouldUseBundlePricing, enableSabreContent, isCharterFlight, dispatch, email, requestLuxPlusPricing])

  return {
    dataStatus,
    initiateRefresh,
    refreshStartedAt,
  }
}

export default useRefreshMultiCartFlightItem
